import {
  EmbeddedAuthResponse,
  EmbeddedCreateAnonParams,
  EmbeddedRegisterApiParams,
  EmbeddedSignInApiParams,
  RefreshTokenParams,
  SignOutParams,
} from "@/types/auth"
import { AxiosInstance } from "axios"

import { BaseApi } from "@/lib/base-api"
import { CfTokenValue } from "@/components/create-anon-turnstile"

export class EmbeddedAuthApi extends BaseApi {
  constructor(axios: AxiosInstance) {
    super(axios, { skipAuthHandling: true })
  }

  async signIn(email: string, password: string, newsletterOptIn?: boolean) {
    return await this.post<EmbeddedAuthResponse, EmbeddedSignInApiParams>(
      `/api/embedded/auth/sign_in`,
      {
        email,
        password,
        organization_email_opt_in: newsletterOptIn,
      }
    )
  }

  async register(params: EmbeddedRegisterApiParams) {
    return await this.post<EmbeddedAuthResponse, EmbeddedRegisterApiParams>(
      `/api/embedded/auth/register`,
      params
    )
  }

  async googleProviderSignIn(code: string, newsletterOptIn?: boolean) {
    return await this.post<
      EmbeddedAuthResponse,
      {
        code: string
        organization_email_opt_in?: boolean
      }
    >(`/api/embedded/auth/provider/google`, {
      code,
      organization_email_opt_in: newsletterOptIn,
    })
  }

  async appleProviderSignIn(params: {
    code: string
    id_token: string
    email?: string
    first_name?: string
    last_name?: string
    organization_email_opt_in?: boolean
  }) {
    return await this.post<
      EmbeddedAuthResponse,
      { code: string; id_token: string }
    >(`/api/embedded/auth/provider/apple`, params)
  }

  async verifyEmailSignIn(verifyCode: string) {
    return await this.post<EmbeddedAuthResponse, { verify_code: string }>(
      `/api/embedded/auth/verify_email_sign_in`,
      { verify_code: verifyCode }
    )
  }

  async createAnonymousUser(
    orgid: string,
    hostname?: string,
    tokenValue?: CfTokenValue,
    code?: string,
    nocookie?: string
  ) {
    return await this.post<EmbeddedAuthResponse, EmbeddedCreateAnonParams>(
      `/api/embedded/auth/create_anonymous_user`,
      { orgid, hostname, ...tokenValue, sso_code: code, nocookie }
    )
  }

  async signOut(params?: SignOutParams): Promise<void> {
    await this.post("/api/embedded/auth/sign_out", params || {})
  }

  async refresh(params: RefreshTokenParams): Promise<EmbeddedAuthResponse> {
    return await this.post<EmbeddedAuthResponse>(
      "/api/embedded/auth/refresh",
      params
    )
  }
}
